import { type UrlObject } from 'url'
import { cva, type VariantProps } from 'class-variance-authority'
import Link from 'next/link'
import { Slot as SlotPrimitive } from 'radix-ui'
import React from 'react'

import Loader from '@/legacy-src/components/Loader'
import { cn } from '@/legacy-src/lib/utils'

// the commented variants are kept here for reference
export const buttonVariants = cva(
  'inline-flex appearance-none items-center justify-center border-2 border-solid border-transparent text-sm font-semibold uppercase no-underline ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'border-primary bg-primary text-white hover:border-peach-600 hover:bg-peach-600',
        light: 'border-sand-400 bg-sand-400 text-secondary hover:border-sand-600 hover:bg-sand-600',
        yellow: 'border-yellow-500 bg-yellow-500 text-secondary hover:border-yellow-600 hover:bg-yellow-600',
        white: 'border-white bg-white text-secondary hover:border-sand-400 hover:bg-sand-400',
        danger: 'border-peach-800 bg-peach-800 text-white hover:border-peach-600 hover:bg-peach-600',
        dark: 'border-petrol-500 bg-petrol-500 text-white hover:border-petrol-400 hover:bg-petrol-400',
        nav: 'justify-start text-petrol-500',
        inline: 'radius-none !h-auto !rounded bg-[transparent] !p-0 text-primary underline-offset-4 hover:underline',
        ghost: '!bg-[transparent] text-primary',
        ghostSand:
          '!bg-[transparent] font-bold text-petrol-500 underline hover:!bg-sand-400 hover:!text-petrol-500 hover:underline',
        muted: 'bg-sand-700 text-white',
        outlineSecondary:
          'border-petrol-500 bg-[transparent] text-petrol-500 hover:border-petrol-300 hover:bg-petrol-300 hover:text-white',
        outlineLanguagePickerFooter:
          'border-petrol-400 bg-[transparent] text-primary hover:border-peach-400 hover:text-primary focus:border-peach-400 focus:text-primary',
      },
      size: {
        text: 'text-base font-semibold normal-case',
        default: 'min-width-[220px] rounded-2xl px-8 py-6 text-base',
        medium: 'rounded-xl px-6 py-4 text-sm',
        small: 'rounded-xl px-3 py-3 text-[13px]',
        icon: 'h-8 w-8 rounded-xl',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        size: 'text',
        className: 'border-none bg-transparent p-0 text-primary hover:bg-transparent hover:text-peach-600',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  newWindow?: boolean
}

/**
 * @deprecated see src/domains/design-system/components/Button/Button.tsx
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, type = 'button', ...props },
    ref,
  ) => {
    const Comp = asChild ? SlotPrimitive.Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
        type={type}
      >
        {loading ? (
          <>
            {/*
              this could potentially cause problems if used with asChild parameter 
              because it would try to merge the props onto the fragment, which would disregard them
              in such case the problematic result would be immediately visible, so we keep this behavior
            */}
            <Loader visible loaderType="button" />
            <span className="w-full flex-shrink-0">{children}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    )
  },
)
// this is needed due to forwardRef being used
Button.displayName = 'Button'

// this is basically a RadixButton that is accepting href as a prop
// in future we can add all props from next/link to this component
/**
 * @deprecated see src/domains/design-system/components/Button/ButtonLink.tsx
 */
const ButtonLink: React.FC<ButtonProps & { href: string | UrlObject; prefetch?: boolean; newWindow?: boolean }> = ({
  href,
  newWindow,
  prefetch,
  ...props
}) => {
  return (
    <Button asChild {...props}>
      <Link
        href={href || '#'}
        target={newWindow && '_blank'}
        rel={newWindow && 'noopener noreferrer'}
        prefetch={prefetch}
      >
        {props.children}
      </Link>
    </Button>
  )
}

export { Button, ButtonLink }
